/**
 * Zod schemas + inferred types for the five TownDesk entities (spec §5 / ADR-0002 entity table)
 * and the public-API input bodies (NFR-11 server-side validation).
 *
 * ISOMORPHIC: this file must stay browser-safe (consumed by @td/widget) — no node:* / aws-sdk.
 * `RequestStatus` is reused from status.ts (the canonical lifecycle).
 */

import { z } from "zod";
import { REQUEST_STATUSES } from "./status.js";

// ---- Field limits (kept modest; the public form is the abuse surface) -------

export const MAX_DESCRIPTION_LEN = 5000;
export const MAX_PHOTOS = 3;
/** Resident intake attachments (photos + PDFs). See intake/spec.md §3.5 ("up to 4"). */
export const MAX_ATTACHMENTS = 4;
export const MAX_NOTE_LEN = 5000;
export const MAX_PUBLIC_UPDATE_LEN = 2000;
export const MAX_NAME_LEN = 200;
export const MAX_ADDRESS_LEN = 500;
/** Max length of an uploaded-file S3 object key accepted on the intake body. */
export const MAX_ATTACHMENT_KEY_LEN = 1024;

// ---- Shared value objects ---------------------------------------------------

export const requestStatusSchema = z.enum(REQUEST_STATUSES);

/** Geo + human address. lat/lng constrained to valid earth ranges. */
export const locationSchema = z
  .object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    address: z.string().max(MAX_ADDRESS_LEN).optional(),
  })
  .strict();

/**
 * Location as captured at intake (ADR-0008, spec §5): an **address / landmark is
 * required**; coordinates are **optional** and only present when the resident taps
 * "use my location". This is the finalized intake shape — the older persisted
 * `locationSchema` (coords-required) is left untouched pending the entity refresh.
 */
export const intakeLocationSchema = z
  .object({
    address: z.string().trim().min(1, "Enter an address or landmark").max(MAX_ADDRESS_LEN),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
  })
  .strict()
  // coords are captured as a pair or not at all
  .refine((l) => (l.lat === undefined) === (l.lng === undefined), {
    message: "lat and lng must be provided together",
  });

export type IntakeLocation = z.infer<typeof intakeLocationSchema>;

/**
 * Reporter contact — PII. Nullable/optional across the model means "anonymous".
 * At least one channel is required *when present* so we never persist an all-empty object.
 */
export const reporterContactSchema = z
  .object({
    name: z.string().max(MAX_NAME_LEN).optional(),
    email: z.string().email().max(320).optional(),
    phone: z.string().max(32).optional(),
  })
  .strict()
  .refine((c) => Boolean(c.name || c.email || c.phone), {
    message: "reporterContact must have at least one of name/email/phone",
  });

export const auditEventTypeSchema = z.enum([
  "status_change",
  "note",
  "assignment",
  "notification_sent",
]);

export const visibilitySchema = z.enum(["public", "internal"]);

export const staffRoleSchema = z.enum(["admin", "staff"]);

// ---- Entities (persisted item shapes) ---------------------------------------

export const requestSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    trackingCode: z.string(),
    category: z.string(),
    description: z.string().max(MAX_DESCRIPTION_LEN),
    location: locationSchema,
    photos: z.array(z.string()).max(MAX_PHOTOS),
    status: requestStatusSchema,
    assigneeId: z.string().nullable().optional(),
    /** null / absent = anonymous report (spec §5). */
    reporterContact: reporterContactSchema.nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict();

export type Request = z.infer<typeof requestSchema>;

export const auditEventSchema = z
  .object({
    id: z.string(),
    requestId: z.string(),
    tenantId: z.string(),
    type: auditEventTypeSchema,
    actorId: z.string().nullable().optional(),
    visibility: visibilitySchema,
    body: z.string(),
    createdAt: z.string(),
  })
  .strict();

export type AuditEvent = z.infer<typeof auditEventSchema>;

export const staffUserSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    email: z.string().email(),
    role: staffRoleSchema,
    name: z.string(),
    active: z.boolean(),
  })
  .strict();

export type StaffUser = z.infer<typeof staffUserSchema>;

export const categorySchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),
    label: z.string(),
    order: z.number(),
    active: z.boolean(),
  })
  .strict();

export type Category = z.infer<typeof categorySchema>;

export const notificationTemplateSchema = z
  .object({
    subject: z.string(),
    body: z.string(),
  })
  .strict();

export const tenantSettingsSchema = z
  .object({
    tenantId: z.string(),
    townName: z.string(),
    logo: z.string().nullable().optional(),
    themeColor: z.string().nullable().optional(),
    notificationTemplates: z.record(z.string(), notificationTemplateSchema).optional(),
    defaultAssignee: z.string().nullable().optional(),
    /** Origins allowed to embed the widget (ADR-0004); empty = none configured yet. */
    allowedEmbedOrigins: z.array(z.string()),
  })
  .strict();

export type TenantSettings = z.infer<typeof tenantSettingsSchema>;

// ---- API input schemas (bodies) — NFR-11 -----------------------------------

/**
 * Public report submission (finalized model — ADR-0002 / spec §5).
 * Tenant is NEVER accepted here — it is resolved server-side from TD_TENANT_ID.
 *
 * - `location`: address required, coords optional (ADR-0008).
 * - `attachments`: the **S3 object keys** of files the client already uploaded via the
 *   presigned-upload path (NFR-10). The body carries keys, not the file bytes; each becomes
 *   an `Attachment` of `kind=resident-photo`, `visibility=public` server-side. Optional →
 *   anonymous, no-attachment reports are valid.
 */
/**
 * INTAKE_SOURCES — the allow-listed referral tags a resident submission may carry (a deep-link
 * `?source=` param, e.g. the city Facebook page's action button → `/report?source=facebook`).
 * This is a REFERRAL tag, distinct from the medium (`IntakeChannel`, which stays "web" for all of
 * these — a Facebook-referred submission still comes through the web form). Allow-listed because the
 * value is URL-controllable and lands in staff exports; an unknown value is dropped, never stored.
 */
export const INTAKE_SOURCES = ["facebook", "qr"] as const;

export const submitReportInputSchema = z
  .object({
    category: z.string().min(1).max(200),
    description: z.string().min(1).max(MAX_DESCRIPTION_LEN),
    location: intakeLocationSchema,
    attachments: z
      .array(z.string().min(1).max(MAX_ATTACHMENT_KEY_LEN))
      .max(MAX_ATTACHMENTS)
      .optional(),
    reporterContact: reporterContactSchema.optional(),
    intakeSource: z.enum(INTAKE_SOURCES).optional(),
  })
  .strict();

export type SubmitReportInput = z.infer<typeof submitReportInputSchema>;

export const changeStatusInputSchema = z
  .object({
    status: requestStatusSchema,
    /** If present, surfaced to the resident on the tracking page (public audit event). */
    publicUpdate: z.string().min(1).max(MAX_PUBLIC_UPDATE_LEN).optional(),
    /** Internal-only reason (e.g. why Invalid/Duplicate). Never shown publicly. */
    reason: z.string().min(1).max(MAX_NOTE_LEN).optional(),
  })
  .strict();

export type ChangeStatusInput = z.infer<typeof changeStatusInputSchema>;

export const assignInputSchema = z
  .object({
    /** null clears the assignment. */
    assigneeId: z.string().min(1).max(200).nullable(),
  })
  .strict();

export type AssignInput = z.infer<typeof assignInputSchema>;

export const addNoteInputSchema = z
  .object({
    body: z.string().min(1).max(MAX_NOTE_LEN),
    visibility: visibilitySchema,
  })
  .strict();

export type AddNoteInput = z.infer<typeof addNoteInputSchema>;

/** Image content types allowed for photo uploads (NFR-10). */
export const ALLOWED_UPLOAD_CONTENT_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;

export const uploadContentTypeSchema = z.enum(ALLOWED_UPLOAD_CONTENT_TYPES);

export const presignedUploadInputSchema = z
  .object({
    contentType: uploadContentTypeSchema,
    /** Original filename or bare extension; used only to derive a safe extension. */
    fileName: z.string().min(1).max(255),
  })
  .strict();

export type PresignedUploadInput = z.infer<typeof presignedUploadInputSchema>;
